import type { ComponentType, SVGProps } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

/**
 * Icon variant styles using Tailwind Variants
 * Provides consistent sizing and coloring for icon components
 */
const icon = tv({
  base: 'flex-shrink-0',
  variants: {
    size: {
      xs: 'h-3 w-3',
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
      xl: 'h-8 w-8',
      '2xl': 'h-10 w-10',
    },
    color: {
      // Default. Adopts the parent's text colour, so an icon in a button or chip
      // never renders grey against white text. See STYLE.md.
      inherit: 'text-current',
      primary: 'text-primary',
      secondary: 'text-secondary',
      success: 'text-success',
      warning: 'text-warning',
      danger: 'text-danger',
      info: 'text-default-600',
      default: 'text-default-400',
      muted: 'text-default-500',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'inherit',
  },
});

export type IconVariants = VariantProps<typeof icon>;

export interface IconProps extends IconVariants, Omit<SVGProps<SVGSVGElement>, 'className' | 'color'> {
  /** The icon component to render (typically from @heroicons/react) */
  as: ComponentType<SVGProps<SVGSVGElement>>;
  /** Additional CSS classes to apply */
  className?: string;
}

/**
 * Polymorphic Icon component with consistent sizing and coloring
 *
 * @example
 * ```tsx
 * import { CheckCircleIcon } from '@heroicons/react/24/outline';
 *
 * // Default size (md) and color (inherit)
 * <Icon as={CheckCircleIcon} />
 *
 * // Custom size and color
 * <Icon as={CheckCircleIcon} size="lg" color="success" />
 *
 * // With accessibility label
 * <Icon as={CheckCircleIcon} aria-label="Success" />
 *
 * // With custom className override
 * <Icon as={CheckCircleIcon} className="custom-class" />
 * ```
 */
export function Icon({ as: Component, size, color, className, ...props }: IconProps) {
  return <Component className={icon({ size, color, className })} {...props} />;
}
